import React from 'react';
import { RotateCcw } from 'lucide-react';
import { Ring, WaveChart, Sparkline } from './charts';
import { sessionUnits, unitsLabel } from '../lib/sessionUnits';

const pl = (n, d = 1) => (n == null ? '—' : Number(n).toFixed(d).replace('.', ','));
const signed = (n, d = 1) => (n == null ? '—' : `${n > 0 ? '+' : n < 0 ? '−' : ''}${pl(Math.abs(n), d)}`);
const Label = ({ children, className = '' }) => <p className={`label-text text-[var(--text-muted)] ${className}`}>{children}</p>;
const Card = ({ className = '', children, ...rest }) => <section className={`glass rounded-[28px] ${className}`} {...rest}>{children}</section>;
const initials = (name) => name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();
const Avatar = ({ name }) => (
  <span className="w-9 h-9 rounded-full bg-[var(--track)] flex items-center justify-center text-[11px] font-bold shrink-0">{initials(name)}</span>
);

export const AttendanceCard = ({ att }) => {
  const max = Math.max(1, ...att.per_day.map((d) => d.count));
  const delta = att.active_this_week - att.active_last_week;
  const frac = att.total_clients ? att.active_this_week / att.total_clients : 0;
  return (
    <Card className="md:col-span-2 p-6 sm:p-8 flex flex-wrap md:flex-nowrap items-center gap-6" data-testid="stat-attendance">
      <div className="flex flex-col gap-3.5 flex-1 basis-40 min-w-0">
        <Label>Frekwencja · ten tydzień</Label>
        <div className="big-num text-6xl">{att.active_this_week}<span className="text-2xl text-[var(--text-muted)] font-bold"> / {att.total_clients}</span></div>
        <p className="text-sm text-[var(--text-muted)]">klientów ćwiczyło · <span className="text-[var(--text-primary)]">{delta >= 0 ? '+' : ''}{delta}</span> vs poprzedni tydzień</p>
      </div>
      <div className="flex-1 basis-48 min-w-0 flex flex-col gap-2.5">
        <div className="flex items-end gap-2.5 h-28">
          {att.per_day.map((d) => (
            <div key={d.day} className="flex-1 h-full flex flex-col items-center justify-end gap-1.5">
              <span className="text-[11px] text-[var(--text-muted)]">{d.count || ''}</span>
              <span className="w-full max-w-[26px] rounded-full" style={{
                height: Math.max(8, (d.count / max) * 88),
                background: d.today ? 'linear-gradient(180deg,#C9A84C,rgba(201,168,76,0.25))' : d.count ? 'var(--chart-main)' : 'var(--track)',
                opacity: d.today || !d.count ? 1 : 0.75,
                boxShadow: d.today ? '0 0 18px rgba(201,168,76,0.45)' : undefined,
              }} />
            </div>
          ))}
        </div>
        <div className="flex gap-2.5">{att.per_day.map((d) => <span key={d.day} className="flex-1 text-center text-[10.5px] text-[var(--text-muted)]">{d.day}</span>)}</div>
      </div>
      <Ring value={frac} size={132} stroke={9}>
        <span className="big-num text-[28px]">{Math.round(frac * 100)}%</span>
        <span className="text-[10px] text-[var(--text-muted)] mt-1">aktywnych</span>
      </Ring>
    </Card>
  );
};

const hhmm = (iso) => new Date(iso).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });

// This month at a glance from the calendar (app sessions + Google Calendar
// events): how many trainings already happened, how many are still planned,
// and the next few coming up.
export const MonthCard = ({ month, today, onOpenCalendar }) => (
  <Card className="md:col-span-2 p-6 sm:p-8 flex flex-col gap-5" data-testid="stat-month">
    <div className="flex flex-wrap items-start justify-between gap-6">
      <div className="flex flex-col gap-3">
        <Label>Treningi · {today.toLocaleDateString('pl-PL', { month: 'long' })}</Label>
        <div className="big-num text-6xl">{month.done}<span className="text-2xl text-[var(--text-muted)] font-bold"> / {month.total}</span></div>
        <p className="text-sm text-[var(--text-muted)]">
          odbytych · <span className="text-[var(--text-primary)]">{month.planned}</span> zaplanowanych do końca miesiąca
        </p>
      </div>
      <Ring value={month.total ? month.done / month.total : 0} size={112} stroke={9}>
        <span className="big-num text-2xl">{month.total ? Math.round((month.done / month.total) * 100) : 0}%</span>
        <span className="text-[10px] text-[var(--text-muted)] mt-1">miesiąca</span>
      </Ring>
    </div>
    <div className="flex flex-col gap-2">
      <Label className="text-[0.6rem]">Najbliższe</Label>
      {month.next.length === 0 ? (
        <p className="text-sm text-[var(--text-muted)]">Brak zaplanowanych treningów.</p>
      ) : month.next.map((s) => (
        <div key={s.id} className="glass-soft rounded-2xl px-4 py-3 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-bold truncate">{s.client_name}</p>
            <p className="text-xs text-[var(--text-muted)]">
              {new Date(s.start_time).toLocaleDateString('pl-PL', { weekday: 'short', day: 'numeric', month: 'short' })} · {hhmm(s.start_time)}
              {sessionUnits(s) > 1 ? ` · ${unitsLabel(sessionUnits(s))}` : ''}
              {s.source === 'google' ? ' · Google' : ''}
            </p>
          </div>
        </div>
      ))}
      <button onClick={onOpenCalendar} className="cta-ghost h-10 rounded-full text-xs font-semibold tracking-[0.2em] uppercase mt-1">Cały kalendarz</button>
    </div>
  </Card>
);

// Weekly revenue at a glance (details on /finanse).
export const RevenueCard = ({ r, onOpen }) => {
  const zl = (n) => `${Math.round(n || 0).toLocaleString('pl-PL')} zł`;
  const cw = r.current_week;
  const vs = r.avg_week ? Math.round(((cw.earned + cw.planned) / r.avg_week - 1) * 100) : null;
  return (
    <Card className="p-6 sm:p-8 flex flex-col gap-3 cursor-pointer hover:border-[#C9A84C]/50 transition-colors" onClick={onOpen} data-testid="stat-revenue">
      <Label>Przychód · tydzień</Label>
      <div className="big-num text-4xl">{zl(cw.earned)}</div>
      <p className="text-[13px] text-[var(--text-muted)]">
        {cw.planned ? `+${zl(cw.planned)} zaplanowane · ` : ''}średnio {zl(r.avg_week)}
        {vs != null && <span className="text-[var(--text-primary)]"> · {vs >= 0 ? '+' : ''}{vs}%</span>}
      </p>
      <Sparkline values={r.weeks.map((w) => w.earned + w.planned)} gold />
    </Card>
  );
};

export const SessionsCard = ({ s }) => (
  <Card className="p-6 sm:p-8 flex flex-col gap-4" data-testid="stat-sessions">
    <Label>Sesje · tydzień</Label>
    <div className="flex items-center gap-5">
      <Ring value={s.total ? s.done / s.total : 0} size={104}>
        <span className="big-num text-2xl">{s.total ? Math.round((s.done / s.total) * 100) : 0}%</span>
      </Ring>
      <div>
        <div className="big-num text-4xl">{s.done}<span className="text-lg text-[var(--text-muted)] font-bold"> / {s.total}</span></div>
        <p className="text-[13px] text-[var(--text-muted)] mt-2">{s.upcoming} zaplanowanych do niedzieli</p>
      </div>
    </div>
  </Card>
);

export const LoadCard = ({ load }) => (
  <Card className="md:col-span-2 p-6 sm:p-8 flex flex-col gap-4" data-testid="stat-load">
    <div className="flex justify-between items-start flex-wrap gap-4">
      <div className="flex flex-col gap-3">
        <Label>Progres obciążeń · 8 tygodni</Label>
        <div className="flex items-baseline gap-3.5 flex-wrap">
          <span className="big-num text-5xl sm:text-[56px] text-[#C9A84C]">{signed(load.avg_pct)}%</span>
          <span className="text-sm text-[var(--text-muted)]">średni wzrost ciężaru roboczego</span>
        </div>
      </div>
      <div className="flex gap-2 flex-wrap">{load.top_exercises.map((e) => <span key={e.exercise} className="chip">{e.exercise} {signed(e.pct, 0)}%</span>)}</div>
    </div>
    <WaveChart id="dash-load" values={load.series} gold height={150} label="Średni wzrost obciążeń w kolejnych tygodniach" />
  </Card>
);

export const WeightCard = ({ w }) => (
  <Card className="p-6 sm:p-8 flex flex-col gap-3.5" data-testid="stat-weight">
    <Label>Efekty · waga</Label>
    <div className="big-num text-5xl">{signed(w.avg_change)}<span className="text-xl text-[var(--text-muted)] font-bold"> kg</span></div>
    <p className="text-[13px] text-[var(--text-muted)] leading-relaxed">
      {w.clients_with_data ? `średnio na klienta · ${w.clients_with_data} z danymi · ${w.reducing_clients} schudło łącznie ${pl(Math.abs(w.total_lost))} kg` : 'Brak pomiarów wagi w ostatnich 8 tygodniach'}
    </p>
    {w.clients_with_data > 0 && <div className="mt-auto"><WaveChart id="dash-weight" values={w.series} height={100} label="Średnia zmiana wagi" /></div>}
  </Card>
);

export const TopProgressCard = ({ top, onOpen }) => (
  <Card className="p-6 sm:p-7 flex flex-col gap-4" data-testid="stat-top">
    <Label>Najlepszy progres</Label>
    {top.length === 0 && <p className="text-sm text-[var(--text-muted)]">Pojawi się po kilku treningach z ciężarem.</p>}
    {top.map((t, i) => (
      <button key={t.client_id} onClick={() => onOpen(t.client_id)} className="flex flex-col gap-2 text-left">
        <span className="flex justify-between w-full text-sm"><span className="font-semibold">{t.name}</span><span className="font-extrabold">{signed(t.pct, 0)}%</span></span>
        <span className="h-1 rounded-full bg-[var(--track)] w-full">
          <span className="block h-1 rounded-full" style={{ width: `${Math.max(6, (t.pct / Math.max(1, top[0].pct)) * 100)}%`, background: i === 0 ? '#C9A84C' : 'var(--chart-main)', boxShadow: i === 0 ? '0 0 10px rgba(201,168,76,0.6)' : undefined }} />
        </span>
        <span className="text-[11.5px] text-[var(--text-muted)]">{t.exercise} {pl(t.from, 0)} → {pl(t.to, 0)} kg</span>
      </button>
    ))}
  </Card>
);

export const AttentionCard = ({ title, items, empty, render, onOpen, testid }) => (
  <Card className="p-6 sm:p-7 flex flex-col gap-3.5" data-testid={testid}>
    <Label>{title}</Label>
    <div className="big-num text-[44px]">{items.length}</div>
    {items.length === 0 && <p className="text-sm text-[var(--text-muted)]">{empty}</p>}
    {items.slice(0, 3).map((it) => (
      <button key={it.client_id + (it.label || '')} onClick={() => onOpen(it.client_id)} className="flex items-center gap-3 text-left">
        <Avatar name={it.name} />
        <span className="flex-1 text-sm truncate">{it.name}</span>
        <span className="text-xs text-[var(--text-muted)] shrink-0">{render(it)}</span>
      </button>
    ))}
  </Card>
);

export const MonthCalendar = ({ sessions, today, selectedDay, onSelectDay }) => {
  const y = today.getFullYear(), m = today.getMonth();
  const offset = (new Date(y, m, 1).getDay() + 6) % 7;
  const days = new Date(y, m + 1, 0).getDate();
  const monthPrefix = `${y}-${String(m + 1).padStart(2, '0')}`;
  const busy = new Set(sessions.filter((s) => s.status !== 'cancelled' && s.start_time.startsWith(monthPrefix)).map((s) => Number(s.start_time.slice(8, 10))));
  const cells = [...Array(offset).fill(null), ...Array.from({ length: days }, (_, i) => i + 1)];
  return (
    <Card className="p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-extrabold tracking-tight capitalize">{today.toLocaleDateString('pl-PL', { month: 'long' })}</h2>
        <span className="text-xs text-[var(--text-muted)]">{y}</span>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center">
        {['Pn', 'Wt', 'Śr', 'Cz', 'Pt', 'So', 'Nd'].map((d) => <span key={d} className="text-[10px] text-[var(--text-muted)] pb-2">{d}</span>)}
        {cells.map((d, i) => {
          const isToday = d === today.getDate();
          const isSelected = d === selectedDay;
          if (!d) return <span key={i} className="h-9" />;
          return (
            <button
              type="button"
              key={i}
              onClick={() => onSelectDay?.(d)}
              aria-pressed={isSelected}
              aria-label={`${new Date(y, m, d).toLocaleDateString('pl-PL', { day: 'numeric', month: 'long' })}${busy.has(d) ? ', są treningi' : ''}`}
              className={`h-9 rounded-xl flex flex-col items-center justify-center gap-[3px] text-[12.5px] ${isToday ? 'nav-active font-extrabold' : d < today.getDate() ? 'text-[var(--text-muted)]' : ''} ${isSelected && !isToday ? 'ring-1 ring-[#C9A84C]' : ''}`}
            >
              {d}
              <span className={`w-1 h-1 rounded-full ${busy.has(d) ? (isToday ? 'bg-[#C9A84C]' : 'bg-[var(--text-muted)]') : ''}`} />
            </button>
          );
        })}
      </div>
    </Card>
  );
};

export const TodaySessions = ({ sessions, date, today, onOpenCalendar }) => {
  const isToday = !date || !today || date.toDateString() === today.toDateString();
  return (
  <Card className="p-6 flex flex-col gap-1">
    <div className="flex justify-between items-center mb-3">
      <h2 className="text-lg font-extrabold tracking-tight">
        {isToday ? 'Dziś' : (() => { const t = date.toLocaleDateString('pl-PL', { weekday: 'long', day: 'numeric', month: 'long' }); return t.charAt(0).toUpperCase() + t.slice(1); })()}
      </h2>
      <span className="chip">{unitsLabel(sessions.reduce((n, s) => n + sessionUnits(s), 0))}</span>
    </div>
    {sessions.length === 0 && <p className="text-sm text-[var(--text-muted)] mb-2">{isToday ? 'Brak sesji na dziś.' : 'Brak sesji tego dnia.'}</p>}
    {sessions.map((s) => {
      const past = new Date(s.end_time) < new Date();
      return (
        <div key={s.id} className="flex gap-4 items-stretch">
          <div className="flex flex-col items-center w-3 pt-1.5">
            <span className={`w-2.5 h-2.5 rounded-full ${past ? 'bg-[var(--text-muted)]' : 'bg-[#C9A84C] shadow-[0_0_10px_rgba(201,168,76,0.8)]'}`} />
            <span className="w-px flex-1 bg-[var(--track)] mt-1.5" />
          </div>
          <div className={`flex-1 px-3.5 py-3 mb-2 rounded-2xl ${past ? 'opacity-60' : 'glass-soft'}`}>
            <div className="flex justify-between gap-2 text-sm font-bold"><span>{s.client_name}</span><span>{new Date(s.start_time).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })}</span></div>
            <div className="text-xs text-[var(--text-muted)] mt-1">
              {sessionUnits(s) > 1 ? `${unitsLabel(sessionUnits(s))} · ` : ''}
              {s.source === 'google' ? 'Google Calendar' : (s.package_label || 'Trening')}{s.location ? ` · ${s.location}` : ''}
            </div>
          </div>
        </div>
      );
    })}
    <button onClick={onOpenCalendar} className="cta-ghost h-11 rounded-full text-xs font-semibold tracking-[0.2em] uppercase mt-2">Otwórz kalendarz</button>
  </Card>
  );
};

const STAGE_KEYS = ['ocena_funkcjonalna', 'identyfikacja_przeszkod', 'plan', 'monitoring_korekty', 'utrzymanie_efektow'];

export const ClientsTable = ({ clients, rows, onOpen, onReset }) => (
  <div className="flex flex-col">
    <div className="hidden md:grid grid-cols-[2fr_1.2fr_1.1fr_1.4fr_0.9fr_40px] gap-4 py-3.5 border-b border-[var(--glass-soft-border)]">
      {['Klient', 'Etap', 'Treningi / tydz.', 'Obciążenie', 'Waga', ''].map((h) => <span key={h} className="label-text text-[var(--text-muted)] text-[0.55rem]">{h}</span>)}
    </div>
    {clients.map((c) => {
      const r = rows[c.id] || {};
      const done = STAGE_KEYS.filter((k) => c.stages?.[k]?.status === 'completed').length;
      const planned = r.planned_per_week || 0;
      return (
        <div key={c.id} role="button" tabIndex={0} onClick={() => onOpen(c.id)} onKeyDown={(e) => e.key === 'Enter' && onOpen(c.id)}
          className="grid grid-cols-[1fr_auto] md:grid-cols-[2fr_1.2fr_1.1fr_1.4fr_0.9fr_40px] gap-x-4 gap-y-2 py-3.5 border-b border-[var(--glass-soft-border)] items-center cursor-pointer hover:bg-[var(--glass-soft)] md:px-2 md:-mx-2 rounded-xl"
          data-testid={`client-card-${c.id}`}>
          <span className="flex items-center gap-3 min-w-0">
            <Avatar name={c.name} />
            <span className="min-w-0">
              <span className="block text-[14.5px] font-semibold truncate">{c.name}</span>
              <span className="block text-[11.5px] text-[var(--text-muted)]">{r.last_training ? `trening ${new Date(r.last_training).toLocaleDateString('pl-PL', { day: 'numeric', month: 'short' })}` : 'brak treningów'}</span>
            </span>
          </span>
          <span className="flex gap-1 justify-end md:justify-start">
            {STAGE_KEYS.map((k, i) => (
              <span key={k} className="w-2 h-2 rounded-full" style={{ background: i < done ? 'var(--chart-main)' : i === done ? '#C9A84C' : 'var(--track)' }} />
            ))}
          </span>
          <span className="hidden md:flex gap-1.5 items-center">
            {planned > 0 ? Array.from({ length: planned }, (_, i) => (
              <span key={i} className="w-5 h-2 rounded" style={{ background: i < r.workouts_this_week ? 'var(--chart-main)' : 'var(--track)' }} />
            )) : <span className="text-xs text-[var(--text-muted)]">{r.workouts_this_week || 0}</span>}
          </span>
          <span className="hidden md:flex items-center gap-2.5">
            <Sparkline values={r.load_spark} width={70} height={22} />
            <span className="text-[13px] font-bold">{r.load_pct != null ? `${signed(r.load_pct, 0)}%` : '—'}</span>
          </span>
          <span className="hidden md:block text-[13px] text-[var(--text-muted)]">{r.weight_change != null ? `${signed(r.weight_change)} kg` : '—'}</span>
          <button onClick={(e) => onReset(e, c)} className="hidden md:flex text-[var(--text-muted)] hover:text-[#C9A84C] p-2" aria-label="Zresetuj hasło klienta" title="Nowy link do hasła" data-testid={`reset-password-${c.id}`}>
            <RotateCcw className="w-4 h-4" />
          </button>
        </div>
      );
    })}
  </div>
);
